import type { FC } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownItem } from 'reactstrap';

import { OBJECTIVE_TYPE_SCOPES } from '../../utils/models/Objective';
import {
  filterValidParentTypes,
  isNoneTypeValid,
  ObjectiveType,
} from '../Objectives/ObjectiveTypeOperations';

interface Props {
  userEditableObjectiveTypes: ObjectiveType[];
  currentTypeId: number | null | undefined;
  onChangeType: (typeId: number | null) => void;
  /** Types assigned to this objective's children — used to filter out
   *  types that would break the hierarchy. */
  childrenTypeIds: (number | null | undefined)[];
  /** Parent objective's type id — used to check if None is valid. */
  parentTypeId: number | null | undefined;
  /** All org types — needed to look up children's link_parent_types. */
  organizationObjectiveTypes: ObjectiveType[];
}

const ObjectiveTypeSelector: FC<Props> = ({
  userEditableObjectiveTypes,
  currentTypeId,
  onChangeType,
  childrenTypeIds,
  parentTypeId,
  organizationObjectiveTypes,
}) => {
  // `undefined` = no parent (root); `null` = individual parent (untyped);
  // a number = strategic parent. Children of an individual parent must
  // also be individual, so no strategic types can be offered.
  const hasIndividualParent = parentTypeId === null;

  const validTypes = useMemo(
    () =>
      hasIndividualParent
        ? []
        : filterValidParentTypes(
            userEditableObjectiveTypes.filter(
              (t) =>
                t.scope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL &&
                (parentTypeId === undefined ||
                  t.link_parent_types?.includes(parentTypeId))
            ),
            childrenTypeIds,
            organizationObjectiveTypes
          ),
    [
      hasIndividualParent,
      userEditableObjectiveTypes,
      childrenTypeIds,
      organizationObjectiveTypes,
      parentTypeId,
    ]
  );

  const canSetNone = useMemo(
    () =>
      isNoneTypeValid(
        parentTypeId,
        childrenTypeIds,
        organizationObjectiveTypes
      ),
    [parentTypeId, childrenTypeIds, organizationObjectiveTypes]
  );

  if (validTypes.length === 0) {
    return null;
  }

  return (
    <>
      <DropdownItem divider />
      <DropdownItem header>
        <FormattedMessage
          id="app.views.person.objective_item_more_controls.change_type"
          defaultMessage="Set type"
        />
      </DropdownItem>
      {validTypes.map((ot) => (
        <DropdownItem
          key={ot.id}
          active={currentTypeId === ot.id}
          onClick={() => onChangeType(ot.id)}
        >
          {ot.name}
        </DropdownItem>
      ))}
      {canSetNone && (
        <DropdownItem
          active={!currentTypeId}
          onClick={() => onChangeType(null)}
        >
          <FormattedMessage
            id="app.views.person.objective_item_more_controls.type_none"
            defaultMessage="Individual"
          />
        </DropdownItem>
      )}
    </>
  );
};

export default ObjectiveTypeSelector;
